import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Cell, ReferenceLine, Legend,
} from 'recharts';
import api from '../api';

const C = {
  bg: '#0a0e27', panel: '#141932', card2: '#1a2045', border: '#252b4a',
  text: '#e4e6ed', muted: '#8b92a8', accent: '#00d4ff',
  ok: '#22c55e', warn: '#fbbf24', err: '#ef4444',
};

function scoreColor(s) {
  if (s >= 70) return C.ok;
  if (s >= 40) return C.warn;
  return C.err;
}

function MiniBar({ value, max, color }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div style={{ background: C.bg, height: 6, borderRadius: 3, overflow: 'hidden', width: 60, display: 'inline-block' }}>
      <div style={{ background: color, width: pct + '%', height: '100%' }} />
    </div>
  );
}

// Forrás-színek (Wikipedia / Wikidata / OSM)
const SRC = { wikipedia: '#22c55e', wikidata: '#00d4ff', osm: '#a855f7' };

// Napi átlag score chart tooltip
function DailyTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: C.text }}>
      <div style={{ color: C.muted, marginBottom: 4 }}>{d.date}</div>
      <div>Átlag score: <b style={{ color: scoreColor(d.score) }}>{d.score}</b> / 100</div>
      <div style={{ color: C.muted }}>{d.narrationCount} narráció</div>
    </div>
  );
}

// Napi forrás-lefedettség chart tooltip (WP / WD / OSM)
function CoverageTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: C.text }}>
      <div style={{ color: C.muted, marginBottom: 4 }}>{d.date}</div>
      <div>📖 Wikipedia (POI-cikk): <b style={{ color: SRC.wikipedia }}>{d.wikipedia}%</b></div>
      <div>📊 Wikidata (tény): <b style={{ color: SRC.wikidata }}>{d.wikidata}%</b></div>
      <div>📍 OSM (tény): <b style={{ color: SRC.osm }}>{d.osm}%</b></div>
      <div style={{ color: C.muted, marginTop: 2 }}>{d.narrationCount} narráció</div>
    </div>
  );
}

// relatív idő-formázás ("3 órája", "2 napja")
function formatRelativeTime(ts) {
  if (!ts) return '—';
  const diff = Date.now() - ts;
  if (diff < 0) return 'jövőben?';
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'most';
  if (minutes < 60) return `${minutes} perce`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} órája`;
  const days = Math.floor(hours / 24);
  return `${days} napja`;
}

const fmtLat = (ms, known) => (ms == null || !known) ? '—' : (ms / 1000).toFixed(1) + 's';

export default function POIQuality() {
  const [hours, setHours] = useState(168);
  const [minCount, setMinCount] = useState(1);
  const [sortBy, setSortBy] = useState('score');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch('/api/poi-quality?hours=' + hours + '&minCount=' + minCount, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'HTTP ' + r.status);
      setData(d);
    } catch (e) {
      setErr(e.message || 'hiba');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [hours, minCount]);

  const pois = data ? data.pois.slice() : [];
  if (sortBy === 'count') pois.sort((a, b) => b.narrationCount - a.narrationCount);
  else if (sortBy === 'name') pois.sort((a, b) => a.name.localeCompare(b.name));
  // 'score' alapból (asc, gyengék előre) — ez a backend alapértelmezett sorrendje

  const weak = data ? data.pois.filter(p => p.score < 40).length : 0;
  const medium = data ? data.pois.filter(p => p.score >= 40 && p.score < 70).length : 0;
  const strong = data ? data.pois.filter(p => p.score >= 70).length : 0;

  // CSV export a szűrt (időszak + min. narráció) POI listáról, a jelenlegi sorrendben.
  function exportCsv() {
    if (!pois.length) return;
    const D = ';';
    const esc = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[";\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const headers = [
      'POI', 'Város', 'Utolsó narráció', 'Narráció', 'Score',
      'Tények (45)', 'Score: WP', 'Score: WD', 'Score: OSM',
      'WP POI-cikk %', 'WP city %', 'WD tény %', 'OSM tény %',
      'Átlag char', 'Score: Hossz (25)',
      'p50 latency (s)', 'Score: Latency (10)', 'Megszakítás %', 'Score: Megszak. (10)', 'Score: Audit (10)',
      'Nyelvek',
    ];
    const rows = pois.map(p => {
      const b = p.scoreBreakdown;
      return [
        p.name, p.city || '',
        p.lastNarrationTs ? new Date(p.lastNarrationTs).toISOString() : '',
        p.narrationCount, p.score,
        b.facts, b.factsWikipedia, b.factsWikidata, b.factsOsm,
        p.wikipedia.articleRate, p.wikipedia.cityRate, p.wikidata.hitRate, p.osm.coverRate,
        p.avgChars, b.length,
        (p.latencyKnown && p.p50FirstChunkMs != null) ? (p.p50FirstChunkMs / 1000).toFixed(1) : '', b.latency,
        p.partialRate, b.partial, b.audit,
        Object.keys(p.languages || {}).join(' '),
      ].map(esc).join(D);
    });
    const csv = '﻿' + [headers.join(D), ...rows].join('\r\n');
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `poi_minoseg_${hours}h_min${minCount}_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  }

  function toggleRow(name) {
    const s = new Set(expanded);
    if (s.has(name)) s.delete(name); else s.add(name);
    setExpanded(s);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>🏆 POI minőség</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, color: C.muted }}>Időszak:&nbsp;
            <select value={hours} onChange={e => setHours(parseInt(e.target.value, 10))}
              style={{ padding: 6, background: C.panel, color: C.text, border: '1px solid ' + C.border, borderRadius: 4 }}>
              <option value="24">24 óra</option>
              <option value="72">3 nap</option>
              <option value="168">7 nap</option>
              <option value="720">30 nap</option>
            </select>
          </label>
          <label style={{ fontSize: 12, color: C.muted }}>Min. narráció:&nbsp;
            <input type="number" value={minCount} min="1" max="100"
              onChange={e => setMinCount(parseInt(e.target.value, 10) || 1)}
              style={{ width: 60, padding: 6, background: C.panel, color: C.text, border: '1px solid ' + C.border, borderRadius: 4 }} />
          </label>
          <label style={{ fontSize: 12, color: C.muted }}>Rendezés:&nbsp;
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              style={{ padding: 6, background: C.panel, color: C.text, border: '1px solid ' + C.border, borderRadius: 4 }}>
              <option value="score">Score (gyengék előre)</option>
              <option value="count">Gyakoriság</option>
              <option value="name">Név</option>
            </select>
          </label>
          <button onClick={exportCsv} disabled={!data || pois.length === 0}
            title="A szűrt POI lista mentése CSV-be (a jelenlegi időszak + min. narráció szerint)"
            style={{ padding: '6px 12px', background: (!data || pois.length === 0) ? C.card2 : C.accent,
              color: (!data || pois.length === 0) ? C.muted : '#04121a', border: 'none', borderRadius: 4,
              fontSize: 12, fontWeight: 600, cursor: (!data || pois.length === 0) ? 'not-allowed' : 'pointer' }}>
            ⬇️ Export CSV
          </button>
        </div>
      </div>

      {err && <div style={{ padding: 12, background: '#3a1f1f', border: '1px solid ' + C.err, borderRadius: 4, color: C.err, marginBottom: 16 }}>⚠️ {err}</div>}

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div className="card"><div style={{ fontSize: 11, color: C.muted }}>Összes POI</div><div style={{ fontSize: 24, fontWeight: 700 }}>{data.totalPois}</div></div>
          <div className="card"><div style={{ fontSize: 11, color: C.muted }}>🔴 Gyenge (&lt;40)</div><div style={{ fontSize: 24, fontWeight: 700, color: C.err }}>{weak}</div></div>
          <div className="card"><div style={{ fontSize: 11, color: C.muted }}>🟡 Közepes (40-69)</div><div style={{ fontSize: 24, fontWeight: 700, color: C.warn }}>{medium}</div></div>
          <div className="card"><div style={{ fontSize: 11, color: C.muted }}>🟢 Jó (≥70)</div><div style={{ fontSize: 24, fontWeight: 700, color: C.ok }}>{strong}</div></div>
        </div>
      )}

      {data && data.daily && data.daily.length > 0 && (() => {
        const avgScore = Math.round(data.daily.reduce((s, d) => s + d.score, 0) / data.daily.length);
        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ fontSize: 14, color: C.muted, margin: 0 }}>📈 Napi átlag score</h3>
              <span style={{ fontSize: 12, color: C.muted }}>Időszak-átlag: <b style={{ color: scoreColor(avgScore) }}>{avgScore}</b> / 100</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.daily.map(d => ({ ...d, label: d.date.slice(5) }))} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="label" tick={{ fill: C.muted, fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fill: C.muted, fontSize: 11 }} />
                <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} content={<DailyTooltip />} />
                <ReferenceLine y={40} stroke={C.err} strokeDasharray="4 4" strokeOpacity={0.5} />
                <ReferenceLine y={70} stroke={C.ok} strokeDasharray="4 4" strokeOpacity={0.5} />
                <Bar dataKey="score" radius={[3, 3, 0, 0]}>
                  {data.daily.map((d, i) => <Cell key={i} fill={scoreColor(d.score)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              Aznap készült narrációk aggregált minőség-pontszáma (az új 100-as modell, napokra bontva, UTC). A szaggatott vonalak a gyenge (40) és jó (70) küszöbök.
            </div>
          </div>
        );
      })()}

      {data && data.daily && data.daily.length > 0 && (() => {
        const avg = (key) => {
          const vals = data.daily.map(d => d[key]).filter(v => v != null);
          return vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0;
        };
        const chartData = data.daily.map(d => ({ ...d, label: d.date.slice(5) }));
        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 4 }}>
              <h3 style={{ fontSize: 14, color: C.muted, margin: 0 }}>🧩 Napi forrás-lefedettség</h3>
              <span style={{ fontSize: 12, color: C.muted }}>
                Átlag — <b style={{ color: SRC.wikipedia }}>WP {avg('wikipedia')}%</b> · <b style={{ color: SRC.wikidata }}>WD {avg('wikidata')}%</b> · <b style={{ color: SRC.osm }}>OSM {avg('osm')}%</b>
              </span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="label" tick={{ fill: C.muted, fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fill: C.muted, fontSize: 11 }} />
                <Tooltip cursor={{ stroke: C.border }} content={<CoverageTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="wikipedia" name="📖 Wikipedia" stroke={SRC.wikipedia} strokeWidth={2} dot={false} connectNulls />
                <Line type="monotone" dataKey="wikidata" name="📊 Wikidata" stroke={SRC.wikidata} strokeWidth={2} dot={false} connectNulls />
                <Line type="monotone" dataKey="osm" name="📍 OSM" stroke={SRC.osm} strokeWidth={2} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              Aznap készült narrációk tény-lefedettsége: Wikipedia = POI-cikk aránya, Wikidata = van Wikidata-tény, OSM = van legalább 1 OSM-tag tény.
            </div>
          </div>
        );
      })()}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ fontSize: 14, color: C.muted, margin: 0 }}>POI lista</h3>
          {loading && <span style={{ color: C.muted, fontSize: 12 }}>Betöltés…</span>}
        </div>

        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: C.muted, textAlign: 'left', borderBottom: '1px solid ' + C.border }}>
              <th style={{ padding: '8px 4px' }}>POI</th>
              <th style={{ padding: '8px 4px', textAlign: 'left' }}>Város</th>
              <th style={{ padding: '8px 4px', textAlign: 'center' }}>Utolsó</th>
              <th style={{ padding: '8px 4px', textAlign: 'right' }}>Narráció</th>
              <th style={{ padding: '8px 4px' }}>Score</th>
              <th style={{ padding: '8px 4px', textAlign: 'center' }}>🧠 Tények /45</th>
              <th style={{ padding: '8px 4px', textAlign: 'center' }}>📏 Hossz</th>
              <th style={{ padding: '8px 4px', textAlign: 'center' }}>⚡ Latency</th>
              <th style={{ padding: '8px 4px', textAlign: 'center' }}>✂️ Megszak.</th>
            </tr>
          </thead>
          <tbody>
            {pois.map(p => {
              const b = p.scoreBreakdown;
              return (
              <React.Fragment key={p.name}>
                <tr onClick={() => toggleRow(p.name)} style={{ cursor: 'pointer', borderBottom: '1px solid ' + C.border }}>
                  <td style={{ padding: '8px 4px' }}>
                    <span style={{ color: C.muted, fontSize: 10, marginRight: 4 }}>{expanded.has(p.name) ? '▾' : '▸'}</span>
                    {p.name}
                  </td>
                  <td style={{ padding: '8px 4px', color: C.muted, fontSize: 11 }}>{p.city || '—'}</td>
                  <td style={{ padding: '8px 4px', textAlign: 'center', color: C.muted, fontSize: 11 }} title={p.lastNarrationTs ? new Date(p.lastNarrationTs).toLocaleString('hu-HU') : ''}>
                    {formatRelativeTime(p.lastNarrationTs)}
                  </td>
                  <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600 }}>{p.narrationCount}</td>
                  <td style={{ padding: '8px 4px' }}>
                    <span style={{ color: scoreColor(p.score), fontWeight: 700, fontSize: 14, marginRight: 8 }}>{p.score}</span>
                    <MiniBar value={p.score} max={100} color={scoreColor(p.score)} />
                  </td>
                  {/* Tények /45 */}
                  <td style={{ padding: '8px 4px', textAlign: 'center', fontSize: 11 }}>
                    <b style={{ color: b.facts >= 30 ? C.ok : b.facts >= 15 ? C.warn : C.err }}>{b.facts}</b>
                    <span style={{ color: C.muted }}> </span>
                    <span style={{ color: SRC.wikipedia }} title="Wikipedia pont /25">📖{b.factsWikipedia}</span>{' '}
                    <span style={{ color: SRC.wikidata }} title="Wikidata pont /10">📊{b.factsWikidata}</span>{' '}
                    <span style={{ color: SRC.osm }} title="OSM pont /10">📍{b.factsOsm}</span>
                  </td>
                  {/* Hossz */}
                  <td style={{ padding: '8px 4px', textAlign: 'center', fontSize: 11 }}>
                    <b style={{ color: b.length >= 20 ? C.ok : b.length >= 12 ? C.warn : C.err }}>{b.length}</b>
                    <span style={{ color: C.muted }}> /25 · {p.avgChars}ch</span>
                  </td>
                  {/* Latency */}
                  <td style={{ padding: '8px 4px', textAlign: 'center', fontSize: 11 }}>
                    <b style={{ color: b.latency >= 8 ? C.ok : b.latency >= 4 ? C.warn : C.err }}>{b.latency}</b>
                    <span style={{ color: C.muted }}> /10 · {fmtLat(p.p50FirstChunkMs, p.latencyKnown)}</span>
                  </td>
                  {/* Megszakítás */}
                  <td style={{ padding: '8px 4px', textAlign: 'center', fontSize: 11 }}>
                    <b style={{ color: b.partial >= 8 ? C.ok : b.partial >= 5 ? C.warn : C.err }}>{b.partial}</b>
                    <span style={{ color: C.muted }}> /10 · {p.partialRate}%</span>
                  </td>
                </tr>
                {expanded.has(p.name) && (
                  <tr style={{ background: C.bg }}>
                    <td colSpan="9" style={{ padding: '12px 16px', borderBottom: '1px solid ' + C.border }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', gap: 16, fontSize: 12 }}>
                        <div>
                          <div style={{ color: C.muted, fontWeight: 600, marginBottom: 4 }}>Score bontás ({p.score} / 100)</div>
                          <div>🧠 Tények: <b style={{ color: C.accent }}>{b.facts}</b> / 45</div>
                          <div style={{ paddingLeft: 12, color: C.muted }}>📖 Wikipedia {b.factsWikipedia}/25 · 📊 Wikidata {b.factsWikidata}/10 · 📍 OSM {b.factsOsm}/10</div>
                          <div>📏 Hossz: <b style={{ color: C.accent }}>{b.length}</b> / 25</div>
                          <div>⚡ Latency: <b style={{ color: C.accent }}>{b.latency}</b> / 10 {!p.latencyKnown && <span style={{ color: C.muted }}>(nincs log-adat → alap)</span>}</div>
                          <div>✂️ Megszakítás: <b style={{ color: C.accent }}>{b.partial}</b> / 10</div>
                          <div>🛡️ Audit: <b style={{ color: C.accent }}>{b.audit}</b> / 10 <span style={{ color: C.muted }}>(egyelőre fix)</span></div>
                        </div>
                        <div>
                          <div style={{ color: C.muted, fontWeight: 600, marginBottom: 4 }}>🧠 Tények lefedettség</div>
                          <div>📖 POI-cikk: {p.wikipedia.articleRate}% · city: {p.wikipedia.cityRate}%</div>
                          <div>📊 Wikidata tény: {p.wikidata.hitRate}% <span style={{ color: C.muted }}>(teljes: {p.wikidata.fullRate}%)</span></div>
                          <div>📍 OSM tény: {p.osm.coverRate}% <span style={{ color: C.muted }}>(≥3 tag: {p.osm.strongRate}%)</span></div>
                        </div>
                        <div>
                          <div style={{ color: C.muted, fontWeight: 600, marginBottom: 4 }}>⚡ Élmény</div>
                          <div>p50 firstChunk: {fmtLat(p.p50FirstChunkMs, p.latencyKnown)}</div>
                          <div>Megszakítás-arány: {p.partialRate}%</div>
                        </div>
                        <div>
                          <div style={{ color: C.muted, fontWeight: 600, marginBottom: 4 }}>Tartalom / nyelv</div>
                          <div>Átlag hossz: {p.avgChars} char</div>
                          <div style={{ marginTop: 4, color: C.muted, fontSize: 11 }}>Nyelvek: {Object.keys(p.languages || {}).join(', ') || '—'}</div>
                        </div>
                      </div>
                      {p.score < 40 && (
                        <div style={{ marginTop: 10, padding: 8, background: '#3a1f1f', borderRadius: 4, fontSize: 11, color: C.err }}>
                          💡 <b>Javaslat:</b> {(() => {
                            if (p.wikipedia.articleRate < 30 && p.osm.coverRate < 30) return 'Kevés a tény: nincs POI-cikk és kevés az OSM-tag. A POI nevét / Wikidata-hozzárendelést és a közeli OSM-adatokat érdemes ellenőrizni.';
                            if (p.wikipedia.articleRate < 30) return 'Nincs POI-szintű Wikipedia-cikk (csak city vagy semmi) — alias vagy Q-szám pontosítás segíthet.';
                            if (p.avgChars < 350) return 'Rövid narráció — a tartalom kevés a célzónához képest.';
                            if (p.latencyKnown && p.p50FirstChunkMs > 8000) return 'Lassú első chunk (>8s) — a forrás-lekérések akadoznak.';
                            if (p.partialRate > 25) return 'Sok a megszakadt narráció — érdemes a stream/timeout viselkedést nézni.';
                            return 'Kombinált okok — nézd a score-bontást fent.';
                          })()}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
            {!loading && pois.length === 0 && (
              <tr><td colSpan="9" style={{ padding: 16, color: C.muted, textAlign: 'center' }}>Nincs POI ebben az időszakban a megadott szűrőkkel.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: C.muted }}>
        💡 <b>Mit mér a score (100 pont)?</b>
        <br />
        🧠 <b>Tények — 45 pt:</b> 📖 Wikipedia (POI-cikk 25 / city-cikk 10 / nincs 0), 📊 Wikidata (van tény 10 / megvolt de 0 item 5 / nincs 0), 📍 OSM-tag tények (≥3 → 10 / 1–2 → 5 / 0 → 0). A Wikivoyage kikerült a pontozásból.
        <br />
        📏 <b>Tartalom — 25 pt:</b> a szöveg hossza sávosan; a 450–750 karakteres célzóna a maximum, a 750 feletti túllépés egyre kevesebb pont (nincs jutalmazva). A célzóna a kért hossz (short/medium) szerint arányosan skálázódik.
        <br />
        ⚡ <b>Élmény — 20 pt:</b> latency (p50 firstChunk ≤3s → 10, felette -2/s) + megszakítás-arány (0% → 10, 50%+ → 0).
        <br />
        🛡️ <b>Audit — 10 pt:</b> egyelőre fix 10 pont mindenkinek (a quality_reports keresztkötése következő lépés).
        <br />
        Küszöbök: 🔴 gyenge &lt;40 · 🟡 közepes 40–69 · 🟢 jó ≥70. A tények/hossz/megszakítás a narration_samples mintákból, a latency a szerver-logokból (POI-névre illesztve) származik.
      </div>
    </div>
  );
}
